import type { ReactNode } from "react";

/**
 * Presentational pieces for the top half of the area page (G3 lane A): the eyebrow section head
 * with its teal tick, the info and chevron glyphs, and the small pills the hero, heatmap and
 * boards share. No hooks, so server and client components can both use them.
 */

/** "This week" / "Resorts" section head: teal tick and fade line, eyebrow, h2 and a lead. */
export function AreaSectionHead({
  id,
  eyebrow,
  title,
  lead,
  className = "",
  tick = true,
}: Readonly<{ id: string; eyebrow: string; title: ReactNode; lead?: ReactNode; className?: string; tick?: boolean }>) {
  return (
    <div className={className}>
      {tick && (
        <div aria-hidden="true" className="mb-3 flex items-center gap-2.5">
          <span className="block h-[3px] w-[22px] shrink-0 rounded-[2px] bg-teal-dark" />
          <span className="block h-px grow bg-[linear-gradient(90deg,rgba(22,128,143,0.35)_0%,rgba(22,35,59,0)_100%)]" />
        </div>
      )}
      <p className="text-[12.5px] font-bold uppercase tracking-[0.12em] text-teal-dark">{eyebrow}</p>
      <h2 id={id} className="mt-1.5 text-[22px] font-bold leading-[1.2] tracking-[-0.01em] text-navy [text-wrap:balance] md:text-[30px] md:leading-[1.15]">
        {title}
      </h2>
      {lead ? <p className="mt-1.5 text-[14px] leading-[1.5] text-[#566072] md:mt-2 md:text-[16px]">{lead}</p> : null}
    </div>
  );
}

/** A 16px chevron (down or right) on a currentColor stroke. */
export function AreaChevron({ direction = "down", size = 16, className = "" }: Readonly<{ direction?: "down" | "right" | "up"; size?: number; className?: string }>) {
  const d = direction === "down" ? "M4 6L8 10L12 6" : direction === "up" ? "M4 10L8 6L12 10" : "M6 3.5L10.5 8L6 12.5";
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" className={`shrink-0 ${className}`}>
      <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The info glyph in the About row's tile. */
export function AreaInfoIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11V16.5" />
      <path d="M12 7.6V7.5" />
    </svg>
  );
}

/** The briefcase glyph beside "Work a season here". */
export function AreaBriefcaseIcon({ size = 17 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="shrink-0">
      <path d="M4 8H20V19H4Z" />
      <path d="M9 8V5.5H15V8" />
      <path d="M4 13H20" />
    </svg>
  );
}

/** A down arrow (16px grid), for links that jump down the page. */
export function AreaArrowDown({ size = 14 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" className="shrink-0">
      <path d="M8 2.5V12.5M4 8.5L8 12.5L12 8.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A small neutral pill ("Out of season", "Closed", "Bike park"). */
export function AreaPill({ children, tone = "neutral", className = "" }: Readonly<{ children: ReactNode; tone?: "neutral" | "teal" | "pale"; className?: string }>) {
  const toneClass = tone === "teal" ? "bg-[#E4F2F3] text-teal-dark" : tone === "pale" ? "bg-[#EEF2EC] text-[#454C5A]" : "bg-[#EDEFF2] text-[#454C5A]";
  return (
    <span className={`inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-full px-2 text-[11.5px] font-semibold ${toneClass} ${className}`}>
      {children}
    </span>
  );
}

const DIFFICULTY_CLASS = {
  beginner: "bg-[#E4F2F3] text-teal-dark",
  intermediate: "bg-white text-[#454C5A] shadow-[inset_0_0_0_1px_#D5DBD2]",
  experienced: "bg-[#F5E8E2] text-[#8E4C30]",
} as const;

/** The ability pill on a surf row. */
export function AreaDifficultyPill({ label, tone }: Readonly<{ label: string; tone: keyof typeof DIFFICULTY_CLASS }>) {
  return (
    <span data-difficulty-tag="" className={`inline-flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-full px-2 text-[12px] font-semibold ${DIFFICULTY_CLASS[tone]}`}>
      {label}
    </span>
  );
}
